/**
 * Local Cache Storage Layer
 * Uses IndexedDB with localStorage fallback
 * Provides normalized data storage for offline-first strategy
 */

import { openDB } from 'idb';

const DB_NAME = 'PlotWyzeCache';
const DB_VERSION = 1;
const STORES = {
  USER: 'user',
  PROJECTS: 'projects',
  CHAPTERS: 'chapters',
  SCENES: 'scenes',
  NOTES: 'notes',
  CORKBOARDS: 'corkboards',
  TIMELINES: 'timelines',
  CHARACTERS: 'characters',
};

let db = null;
let useIndexedDB = true;

/**
 * Initialize IndexedDB
 */
const initDB = async () => {
  if (db) return db;

  try {
    db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(database) {
        // Create object stores if they don't exist
        if (!database.objectStoreNames.contains(STORES.USER)) {
          database.createObjectStore(STORES.USER, { keyPath: '_id' });
        }
        if (!database.objectStoreNames.contains(STORES.PROJECTS)) {
          const projectStore = database.createObjectStore(STORES.PROJECTS, { keyPath: '_id' });
          projectStore.createIndex('userId', 'thinOwner.id', { unique: false });
        }
        if (!database.objectStoreNames.contains(STORES.CHAPTERS)) {
          const chapterStore = database.createObjectStore(STORES.CHAPTERS, { keyPath: '_id' });
          chapterStore.createIndex('projectId', 'projectId', { unique: false });
        }
        if (!database.objectStoreNames.contains(STORES.SCENES)) {
          const sceneStore = database.createObjectStore(STORES.SCENES, { keyPath: '_id' });
          sceneStore.createIndex('chapterId', 'chapterId', { unique: false });
        }
        if (!database.objectStoreNames.contains(STORES.NOTES)) {
          const noteStore = database.createObjectStore(STORES.NOTES, { keyPath: '_id' });
          noteStore.createIndex('projectId', 'projectId', { unique: false });
        }
        if (!database.objectStoreNames.contains(STORES.CORKBOARDS)) {
          const corkboardStore = database.createObjectStore(STORES.CORKBOARDS, { keyPath: '_id' });
          corkboardStore.createIndex('projectId', 'projectId', { unique: false });
        }
        if (!database.objectStoreNames.contains(STORES.TIMELINES)) {
          const timelineStore = database.createObjectStore(STORES.TIMELINES, { keyPath: '_id' });
          timelineStore.createIndex('projectId', 'projectId', { unique: false });
        }
        if (!database.objectStoreNames.contains(STORES.CHARACTERS)) {
          const characterStore = database.createObjectStore(STORES.CHARACTERS, { keyPath: 'id' });
          characterStore.createIndex('projectId', 'projectId', { unique: false });
        }
      },
    });
    useIndexedDB = true;
    return db;
  } catch (error) {
    console.warn('[localCache] IndexedDB not available, falling back to localStorage:', error);
    useIndexedDB = false;
    return null;
  }
};

/**
 * Get store name for entity type
 */
const getStoreName = (entityType) => {
  const storeMap = {
    user: STORES.USER,
    projects: STORES.PROJECTS,
    chapters: STORES.CHAPTERS,
    scenes: STORES.SCENES,
    notes: STORES.NOTES,
    corkboards: STORES.CORKBOARDS,
    timelines: STORES.TIMELINES,
    characters: STORES.CHARACTERS,
  };
  return storeMap[entityType];
};

/**
 * localStorage fallback helpers
 */
const getLocalStorageKey = (storeName, key) => {
  return `cache_${storeName}_${key}`;
};

const getLocalStorageStoreKey = (storeName) => {
  return `cache_${storeName}_store`;
};

const localStorageGet = (storeName, key) => {
  try {
    const itemKey = key ? getLocalStorageKey(storeName, key) : getLocalStorageStoreKey(storeName);
    const data = localStorage.getItem(itemKey);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('[localCache] Error reading from localStorage:', error);
    return null;
  }
};

const localStorageSet = (storeName, key, value) => {
  try {
    const itemKey = key ? getLocalStorageKey(storeName, key) : getLocalStorageStoreKey(storeName);
    localStorage.setItem(itemKey, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error('[localCache] Error writing to localStorage:', error);
    return false;
  }
};

const localStorageDelete = (storeName, key) => {
  try {
    const itemKey = key ? getLocalStorageKey(storeName, key) : getLocalStorageStoreKey(storeName);
    localStorage.removeItem(itemKey);
    return true;
  } catch (error) {
    console.error('[localCache] Error deleting from localStorage:', error);
    return false;
  }
};

const localStorageGetAll = (storeName) => {
  try {
    const prefix = `cache_${storeName}_`;
    const allItems = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix) && key.endsWith('_store') === false) {
        const item = localStorage.getItem(key);
        if (item) {
          allItems.push(JSON.parse(item));
        }
      }
    }
    return allItems;
  } catch (error) {
    console.error('[localCache] Error reading all from localStorage:', error);
    return [];
  }
};

const localStorageClearStore = (storeName) => {
  try {
    const prefix = `cache_${storeName}_`;
    const keysToDelete = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach((key) => localStorage.removeItem(key));
    return true;
  } catch (error) {
    console.error('[localCache] Error clearing localStorage store:', error);
    return false;
  }
};

/**
 * Get a single item from cache
 */
export const getCachedItem = async (entityType, key) => {
  const storeName = getStoreName(entityType);
  if (!storeName) {
    console.error('[localCache] Unknown entity type:', entityType);
    return null;
  }

  await initDB();

  if (useIndexedDB && db) {
    try {
      const item = await db.get(storeName, key);
      return item || null;
    } catch (error) {
      console.error('[localCache] Error reading from IndexedDB:', error);
      return null;
    }
  }

  // Fallback to localStorage
  return localStorageGet(storeName, key);
};

/**
 * Get all items from a store (optionally filtered by index)
 */
export const getCachedItems = async (entityType, filter = null) => {
  const storeName = getStoreName(entityType);
  if (!storeName) {
    console.error('[localCache] Unknown entity type:', entityType);
    return [];
  }

  await initDB();

  if (useIndexedDB && db) {
    try {
      if (filter && filter.index && filter.value !== undefined) {
        // Filter by index
        const index = db.transaction(storeName, 'readonly').store.index(filter.index);
        const items = await index.getAll(filter.value);
        return items || [];
      }
      // Get all items
      const items = await db.getAll(storeName);
      return items || [];
    } catch (error) {
      console.error('[localCache] Error reading all from IndexedDB:', error);
      return [];
    }
  }

  // Fallback to localStorage
  const allItems = localStorageGetAll(storeName);
  if (filter && filter.index && filter.value !== undefined) {
    // Filter in memory
    return allItems.filter((item) => item[filter.index] === filter.value);
  }
  return allItems;
};

/**
 * Set/update a single item in cache
 */
export const setCachedItem = async (entityType, item) => {
  const storeName = getStoreName(entityType);
  if (!storeName) {
    console.error('[localCache] Unknown entity type:', entityType);
    return false;
  }

  if (!item || (!item._id && !item.id)) {
    console.error('[localCache] Item missing ID:', item);
    return false;
  }

  const key = item._id || item.id;

  await initDB();

  if (useIndexedDB && db) {
    try {
      await db.put(storeName, item);
      return true;
    } catch (error) {
      console.error('[localCache] Error writing to IndexedDB:', error);
      return false;
    }
  }

  // Fallback to localStorage
  return localStorageSet(storeName, key, item);
};

/**
 * Set multiple items in cache (batch update)
 */
export const setCachedItems = async (entityType, items) => {
  if (!Array.isArray(items)) {
    console.error('[localCache] Expected array of items');
    return false;
  }

  const storeName = getStoreName(entityType);
  if (!storeName) {
    console.error('[localCache] Unknown entity type:', entityType);
    return false;
  }

  await initDB();

  if (useIndexedDB && db) {
    try {
      const tx = db.transaction(storeName, 'readwrite');
      const promises = items.map((item) => tx.store.put(item));
      await Promise.all(promises);
      await tx.done;
      return true;
    } catch (error) {
      console.error('[localCache] Error batch writing to IndexedDB:', error);
      return false;
    }
  }

  // Fallback to localStorage
  const results = items.map((item) => {
    const key = item._id || item.id;
    return localStorageSet(storeName, key, item);
  });
  return results.every((r) => r === true);
};

/**
 * Delete a single item from cache
 */
export const deleteCachedItem = async (entityType, key) => {
  const storeName = getStoreName(entityType);
  if (!storeName) {
    console.error('[localCache] Unknown entity type:', entityType);
    return false;
  }

  await initDB();

  if (useIndexedDB && db) {
    try {
      await db.delete(storeName, key);
      return true;
    } catch (error) {
      console.error('[localCache] Error deleting from IndexedDB:', error);
      return false;
    }
  }

  // Fallback to localStorage
  return localStorageDelete(storeName, key);
};

/**
 * Clear all items from a store
 */
export const clearCachedStore = async (entityType) => {
  const storeName = getStoreName(entityType);
  if (!storeName) {
    console.error('[localCache] Unknown entity type:', entityType);
    return false;
  }

  await initDB();

  if (useIndexedDB && db) {
    try {
      await db.clear(storeName);
      return true;
    } catch (error) {
      console.error('[localCache] Error clearing IndexedDB store:', error);
      return false;
    }
  }

  // Fallback to localStorage
  return localStorageClearStore(storeName);
};

/**
 * Clear all cache for a user (all stores)
 */
export const clearAllCache = async () => {
  await initDB();

  const stores = Object.values(STORES);
  const results = await Promise.all(stores.map((storeName) => clearCachedStore(storeName)));

  return results.every((r) => r === true);
};

/**
 * Get cache metadata (for debugging)
 */
export const getCacheStats = async () => {
  await initDB();

  const stats = {
    storageType: useIndexedDB ? 'IndexedDB' : 'localStorage',
    stores: {},
  };

  const stores = Object.values(STORES);
  const storeStats = await Promise.all(
    stores.map(async (storeName) => {
      const items = await getCachedItems(storeName);
      return { storeName, count: items.length };
    })
  );

  storeStats.forEach(({ storeName, count }) => {
    stats.stores[storeName] = count;
  });

  return stats;
};

export default {
  getItem: getCachedItem,
  getItems: getCachedItems,
  setItem: setCachedItem,
  setItems: setCachedItems,
  deleteItem: deleteCachedItem,
  clearStore: clearCachedStore,
  clearAll: clearAllCache,
  getStats: getCacheStats,
};
